<template>
  <div
    ref="root"
    class="relative isolate flex min-h-[calc(100vh-var(--topbar-height))] items-start justify-center overflow-hidden bg-white px-4 pb-3 pt-6 md:pt-8"
    data-testid="welcome"
  >
    <svg
      class="pointer-events-none absolute left-1/2 top-0 -z-10 overflow-visible text-primary-100"
      width="1"
      height="1"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id="welcome-glow"
          x1="0"
          y1="724"
          x2="0"
          y2="-724"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="currentColor" />
          <stop
            offset="0.43"
            stop-color="white"
          />
        </linearGradient>
      </defs>
      <ellipse
        cx="-352"
        cy="0"
        rx="1136"
        ry="724"
        fill="url(#welcome-glow)"
        opacity="0.3"
      />
      <g
        class="stroke-purple-200"
        fill="none"
        stroke-width="2"
        opacity="0.4"
      >
        <ellipse
          cx="-1014"
          cy="20.5"
          rx="823"
          ry="551.5"
        />
        <ellipse
          cx="912"
          cy="804"
          rx="823"
          ry="723"
        />
      </g>
    </svg>

    <div
      class="max-md:hidden"
      :style="{ width: `${(STAGE_WIDTH / 10) * scale}rem`, height: `${(STAGE_HEIGHT / 10) * scale}rem` }"
    >
      <div
        class="relative h-[61.1rem] w-[87.2rem] origin-top-left"
        :style="{ transform: `scale(${scale})` }"
      >
        <div class="absolute inset-x-0 -top-24 h-[70.9rem]">
          <div
            class="welcome-rise absolute left-32 top-24 flex w-[64.4rem] flex-col items-center gap-3 text-center"
            style="--d: 0ms"
          >
            <h1 class="w-full text-4xl font-medium text-gray-900">{{ t('title') }}</h1>
            <i18n-t
              keypath="body"
              tag="p"
              class="w-full text-md text-gray-900"
            >
              <template #assistants><b class="font-bold">{{ t('assistants') }}</b></template>
              <template #contractors><b class="font-bold">{{ t('contractors') }}</b></template>
              <template #remote><b class="font-bold">{{ t('remote') }}</b></template>
            </i18n-t>
          </div>

          <div
            role="img"
            :aria-label="t('scene_label')"
            data-testid="welcome-scene"
          >
            <div
              v-for="grid in DOT_GRIDS"
              :key="grid.position"
              :class="['welcome-dots absolute grid size-20 grid-cols-8 content-between justify-between', grid.position]"
              :style="{ '--d': grid.delay }"
            >
              <span
                v-for="n in 64"
                :key="n"
                class="size-[0.3rem] bg-primary-700"
              />
            </div>
            <div class="absolute left-[48rem] top-[27.2rem] -translate-x-1/2">
              <div
                class="welcome-rise flex items-start gap-5 rounded-xl bg-white p-5 drop-shadow-md drop-shadow-indigo-400/10"
                style="--d: 150ms"
              >
                <div
                  v-for="(day, index) in DAYS"
                  :key="day.day"
                  class="flex flex-col items-center"
                >
                  <div class="flex flex-col items-center gap-2">
                    <p class="whitespace-nowrap text-center text-sm text-gray-800">{{ day.day }}</p>
                    <div class="relative h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
                      <div
                        class="welcome-grow absolute inset-y-0 left-0 rounded-full bg-blue-500"
                        :style="{ width: `${day.fill / 10}rem`, '--d': `${500 + index * 90}ms` }"
                      />
                    </div>
                  </div>
                  <p
                    class="whitespace-nowrap text-center text-sm leading-7"
                    :class="day.time ? 'font-bold text-gray-700' : 'text-gray-400'"
                  >
                    {{ day.time || '0:00' }}
                  </p>
                  <span
                    v-if="day.time"
                    class="welcome-pop flex h-5 items-center justify-center rounded-full px-1.5 text-xs leading-5 text-white"
                    :class="day.low ? 'bg-orange-500' : 'bg-green-400'"
                    :style="{ '--d': `${800 + index * 90}ms` }"
                  >{{ day.percent }}</span>
                  <span
                    v-else
                    class="flex h-5 w-7 items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs leading-5 text-gray-900"
                  >-</span>
                </div>
              </div>
            </div>

            <div
              v-for="person in PEOPLE"
              :key="person.name"
              class="absolute flex items-center gap-5 rounded-xl bg-white px-5 py-3 drop-shadow-md drop-shadow-indigo-400/10"
              :class="person.position"
              :style="{ '--d': person.delay }"
            >
              <div class="relative size-16 shrink-0">
                <div
                  class="absolute inset-0 overflow-hidden rounded-full"
                  :class="person.gradient"
                >
                  <img
                    :src="person.photo"
                    alt=""
                    class="absolute top-1 size-full max-w-none object-cover"
                    :class="person.photoClass"
                  />
                </div>
              </div>
              <div class="flex flex-col items-start gap-1">
                <p class="whitespace-nowrap text-lg font-medium leading-5 text-gray-900">{{ person.name }}</p>
                <div class="flex items-center gap-1">
                  <span
                    class="flex size-5 items-center justify-center rounded-full"
                    :class="person.project ? 'bg-green-500/30' : 'bg-gray-400/30'"
                  >
                    <span
                      class="size-2.5 rounded-full"
                      :class="person.project ? 'bg-green-500' : 'bg-gray-400'"
                    />
                  </span>
                  <i18n-t
                    v-if="person.project"
                    keypath="working_on"
                    tag="p"
                    class="whitespace-nowrap text-sm text-gray-500"
                  >
                    <template #project><b class="font-bold text-gray-900">{{ person.project }}</b></template>
                  </i18n-t>
                  <p
                    v-else
                    class="whitespace-nowrap text-sm text-gray-500"
                  >
                    {{ t('not_working') }}
                  </p>
                </div>
              </div>
            </div>

            <div
              v-for="shot in SCREENSHOTS"
              :key="shot.position"
              class="absolute h-24 w-36"
              :class="shot.position"
            >
              <div
                class="absolute inset-0 rounded-md shadow-md shadow-primary-900/20"
                :class="shot.float"
                :style="{ '--d': shot.delay }"
              >
                <img
                  :src="shot.src"
                  alt=""
                  class="absolute inset-0 size-full rounded-md object-cover"
                />
              </div>
              <span
                class="welcome-pop absolute bottom-0 left-1/2 flex h-5 w-9 -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full bg-green-400 px-1.5 text-xs font-medium leading-5 text-white shadow-md shadow-green-400/25"
              >{{ shot.percent }}</span>
            </div>
          </div>

          <ZoneButton
            color="primary_alt"
            size-class="h-12 px-4 text-sm font-medium!"
            class="welcome-rise absolute left-[37.2rem] top-[65.9rem] w-44 justify-center"
            style="--d: 900ms"
            data-testid="welcome-start"
            @click="$emit('start')"
          >
            {{ t('get_started') }}
          </ZoneButton>
        </div>
      </div>
    </div>

    <div class="flex w-full max-w-96 flex-col items-center gap-4 text-center md:hidden">
      <h1 class="text-3xl font-medium text-gray-900">{{ t('title') }}</h1>
      <i18n-t
        keypath="body"
        tag="p"
        class="text-md text-gray-900"
      >
        <template #assistants><b class="font-bold">{{ t('assistants') }}</b></template>
        <template #contractors><b class="font-bold">{{ t('contractors') }}</b></template>
        <template #remote><b class="font-bold">{{ t('remote') }}</b></template>
      </i18n-t>
      <ZoneButton
        color="primary_alt"
        size-class="h-12 px-4 text-sm font-medium!"
        class="mt-2 w-full justify-center"
        data-testid="welcome-start-mobile"
        @click="$emit('start')"
      >
        {{ t('get_started') }}
      </ZoneButton>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneButton from 'zone/components/base/Button.vue';
import {
  AR0239_WELCOME_ADRIAN,
  AR0239_WELCOME_MADELINE,
  AR0239_WELCOME_SCREENSHOT_1,
  AR0239_WELCOME_SCREENSHOT_2,
} from '~/rails_assets';

const STAGE_WIDTH = 872;
const STAGE_HEIGHT = 611;
const SPACE_ABOVE_AND_BELOW = 90;
const SIDE_PADDING = 32;

const DOT_GRIDS = [
  { position: 'left-0 top-[35.2rem]', delay: '0s' },
  { position: 'left-[76.2rem] top-[47.9rem]', delay: '-1.4s' },
];

const DAYS = [
  { day: 'Mon, Aug 20', fill: 72, time: '7:25', percent: '51%' },
  { day: 'Tue, Aug 21', fill: 72, time: '8:01', percent: '66%' },
  { day: 'Wed, Aug 22', fill: 48, time: '6:40', percent: '36%', low: true },
  { day: 'Thu, Aug 23', fill: 0 },
  { day: 'Fri, Aug 24', fill: 96, time: '8:18', percent: '71%' },
  { day: 'Sat, Aug 25', fill: 0 },
  { day: 'Sun, Aug 26', fill: 0 },
];

const PEOPLE = [
  {
    name: 'Adrian Goia',
    project: 'Product Design',
    photo: AR0239_WELCOME_ADRIAN,
    gradient: 'bg-linear-to-br from-teal-400 to-green-400',
    photoClass: 'scale-105 -rotate-2',
    position: 'welcome-float-a left-56 top-[41.2rem]',
    delay: '350ms',
  },
  {
    name: 'Madeline Peterson',
    photo: AR0239_WELCOME_MADELINE,
    gradient: 'bg-linear-to-t from-yellow-400 to-yellow-300',
    position: 'welcome-float-b left-96 top-[51rem] w-80',
    delay: '500ms',
  },
];

const SCREENSHOTS = [
  {
    src: AR0239_WELCOME_SCREENSHOT_1,
    position: 'left-14 top-[45.8rem]',
    float: 'welcome-float-b',
    delay: '650ms',
    percent: '60%',
  },
  {
    src: AR0239_WELCOME_SCREENSHOT_2,
    position: 'left-[73.2rem] top-[41.2rem]',
    float: 'welcome-float-a',
    delay: '800ms',
    percent: '71%',
  },
];

defineEmits(['start']);

defineOptions({ name: 'ChecklistWelcome' });

const { t } = useI18n();

const root = useTemplateRef('root');
const scale = ref(1);
let observer = null;

const fit = () => {
  const width = root.value.clientWidth - SIDE_PADDING;
  const height = window.innerHeight - SPACE_ABOVE_AND_BELOW;

  scale.value = Math.max(0.5, Math.min(1, width / STAGE_WIDTH, height / STAGE_HEIGHT));
};

onMounted(() => {
  fit();
  observer = new ResizeObserver(fit);
  observer.observe(root.value);
});

onBeforeUnmount(() => observer.disconnect());
</script>

<style scoped>
@keyframes welcome-rise { from { opacity: 0; translate: 0 1.4rem; } to { opacity: 1; translate: 0 0; } }
@keyframes welcome-grow { from { scale: 0 1; } to { scale: 1 1; } }
@keyframes welcome-pop { 0% { opacity: 0; scale: 0.5; } 65% { scale: 1.12; } 100% { opacity: 1; scale: 1; } }
@keyframes welcome-bob { 0%, 100% { translate: 0 0; } 50% { translate: 0 -0.7rem; } }
@keyframes welcome-dots { 0%, 100% { opacity: 0.45; } 50% { opacity: 1; } }

.welcome-rise { animation: welcome-rise 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms) both; }
.welcome-grow { transform-origin: left; animation: welcome-grow 0.8s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms) both; }
.welcome-pop { animation: welcome-pop 0.5s ease-out var(--d, 0ms) both; }
.welcome-dots { animation: welcome-dots 3.2s ease-in-out var(--d, 0s) infinite; }

.welcome-float-a {
  animation:
    welcome-rise 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms) both,
    welcome-bob 6s ease-in-out calc(var(--d, 0ms) + 1s) infinite;
}

.welcome-float-b {
  animation:
    welcome-rise 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) var(--d, 0ms) both,
    welcome-bob 7.5s ease-in-out calc(var(--d, 0ms) + 1.6s) infinite reverse;
}

@media (prefers-reduced-motion: reduce) {
  .welcome-rise, .welcome-grow, .welcome-pop, .welcome-dots, .welcome-float-a, .welcome-float-b { animation: none; }
}
</style>

<i18n lang="json">
{
  "en": {
    "title": "Manage your team without micromanaging.",
    "body": "In just a few minutes, you'll have clear visibility into your team's work — without constant check-ins. Built for teams with {assistants}, {contractors}, and {remote}.",
    "assistants": "virtual assistants",
    "contractors": "contractors",
    "remote": "remote employees",
    "working_on": "Working on {project}",
    "not_working": "Not working",
    "get_started": "Get started",
    "scene_label": "Preview of Hubstaff: weekly tracked time, team members' status and activity screenshots"
  }
}
</i18n>
